import { AppSkinProps, CriteriaConstraint } from '../../../../types/decisions';
import React, { useCallback, useEffect } from 'react';
import { Autocomplete, Button, FormControl, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { TriangleSlider } from '../../../slider';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';

const AppCriteriaTypeText = ({
    id,
    importance,
    value,
    hideValues,
    hideImportance,
    onSliderChange,
    onTradeoffValueChange,
    showAddConstraint,
    setCriteriaConstraints,
    criteriaConstraints,
    disabled = false,
    suggestedValues,
    variant = 'exact',
}: {
    disabled?: boolean;
    importance: number;
    value: string;
    showAddConstraint?: boolean;
    hideImportance?: boolean;
    hideField?: boolean;
    hideValues?: boolean;
    onSliderChange: AppSkinProps['onSliderChange'];
    onTradeoffValueChange: (value: any, id: string) => void;
    id: string;
    criteriaConstraints: {
        [key: string]: CriteriaConstraint[]
    },
    setCriteriaConstraints: (value: (((prevState: {
        [p: string]: CriteriaConstraint[]
    }) => {
        [p: string]: CriteriaConstraint[]
    }) | {
                                         [p: string]: CriteriaConstraint[]
                                     })) => void,
    suggestedValues?: string[],
    variant?: 'list' | 'exact'
}) => {
    useEffect(() => {
        if (!criteriaConstraints[id]) {
            setCriteriaConstraints(criteriaConstraints => (
                { ...criteriaConstraints, [id]: [] }
            ));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onConstraintChange = (value: string, index: number, key: 'value' | 'rule') => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }
                return {
                    ...constraint,
                    [key]: value,
                };
            });
            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onConstraintOperatorChange = (value: 'and' | 'or', index: number) => {
        setCriteriaConstraints((constraints) => {
            const updatedConstraints = constraints[id].map((constraint, idx) => {
                if (idx !== index) {
                    return constraint;
                }
                return {
                    ...constraint,
                    operator: value,
                };
            });
            return {
                ...constraints,
                [id]: updatedConstraints,
            };
        });
    };

    const onAddConstraint = () => {
        setCriteriaConstraints(constraints => (
            {
                ...constraints,
                [id]: [ ...constraints[id], {
                    value: '',
                    rule: 'em',
                    operator: constraints[id].length > 0 ? 'and' : null,
                } ],
            }
        ));
    };

    const onDeleteConstraint = (index: number) => {
        setCriteriaConstraints(constraints => {
            const filtered = constraints[id].filter((_, idx) => idx !== index);
            if (!filtered.length) {
                return {
                    ...constraints,
                    [id]: [],
                };
            }
            filtered[0] = {
                ...filtered[0],
                operator: null,
            };
            return {
                ...constraints,
                [id]: filtered,
            };
        });
    };

    type Option = {
        value: string;
        options?: Option[];
    };

    const [ hierarchy, setHierarchy ] = React.useState<Option[]>([]);
    const [ path, setPath ] = React.useState<Option[]>([]);

    const currentOptions =
        path.length === 0 ? hierarchy : path[path.length - 1].options || [];

    const handleSelect = (_: any, newValue: Option | null, level: number) => {
        if (!newValue) {
            return;
        }

        const newPath = [ ...path.slice(0, level), newValue ];
        setPath(newPath);

        const fullPath = newPath.map(p => p.value).join(', ');
        onTradeoffValueChange(fullPath, id);

        if (newValue.options && newValue.options.length > 0) {
            const firstChild = newValue.options[0];
            handleSelect(null, firstChild, level + 1);
            return;
        }
    };

    const buildHierarchy = useCallback((strings: string[]): Option[] => {
        const root: Option[] = [];
        strings.forEach((str) => {
            const parts = str.split(',').map((s) => s.trim());
            let currentLevel = root;
            parts.forEach((part, i) => {
                let existing = currentLevel.find((o) => o.value === part);
                if (!existing) {
                    existing = { value: part, options: [] };
                    currentLevel.push(existing);
                }
                if (i < parts.length - 1) {
                    if (!existing.options) {
                        existing.options = [];
                    }
                    currentLevel = existing.options;
                }
            });
        });
        return root;
    }, []);

    React.useEffect(() => {
        if (suggestedValues && suggestedValues.length > 0) {
            const h = buildHierarchy(suggestedValues);
            setHierarchy(h);

            if (value) {
                const parts = value.split(',').map(s => s.trim());
                let currentLevel = h;
                const restoredPath: Option[] = [];

                parts.forEach((part) => {
                    const found = currentLevel.find(o => o.value === part);
                    if (found) {
                        restoredPath.push(found);
                        currentLevel = found.options || [];
                    }
                });

                setPath(restoredPath);
            }
        }
    }, [ suggestedValues, value, buildHierarchy ]);

    return (
        <div>
            {hideValues ? null : (
                <div style={{ marginBottom: 10 }}>
                    {variant === 'list' && hierarchy.length > 0 ? (
                        <div>
                            {path.map((selected, i) => (
                                <Autocomplete
                                    key={i}
                                    value={selected}
                                    options={(
                                        i === 0 ? hierarchy : path[i - 1].options
                                    ) || []}
                                    getOptionLabel={(option) => option.value}
                                    onChange={(e, val) => handleSelect(e, val, i)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={
                                                (
                                                    (
                                                        i === 0 ? hierarchy : path[i - 1].options
                                                    ) || []
                                                )[0]?.value || ''
                                            }
                                            fullWidth
                                            size="small"
                                            disabled={disabled}
                                        />
                                    )}
                                    sx={{ mb: 2 }}
                                />
                            ))}

                            {currentOptions.length > 0 && (
                                <Autocomplete
                                    value={path[path.length] || currentOptions[0]}
                                    options={currentOptions}
                                    getOptionLabel={(option) => option.value}
                                    onChange={(e, val) => handleSelect(e, val, path.length)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={currentOptions[0]?.value || ''}
                                            fullWidth
                                            size="small"
                                            disabled={disabled}
                                        />
                                    )}
                                />
                            )}
                        </div>
                    ) : (
                        <TextField
                            defaultValue={value}
                            fullWidth
                            size="small"
                            onBlur={(e) => onTradeoffValueChange(e.target.value, id)}
                        />
                    )}
                </div>
            )}
            {hideImportance ? null : (
                <div>
                    <TriangleSlider
                        min={0}
                        max={100}
                        step={1}
                        value={importance}
                        leftText="Not Important"
                        rightText="Really Important"
                        onChange={(_, value) => onSliderChange(value as number, id)}
                        disabled={disabled}
                    />
                </div>
            )}
            {!showAddConstraint ? null : (
                <div style={{ marginTop: 20 }}>
                    <div style={{ display: criteriaConstraints[id].length > 0 ? 'block' : 'none' }}>
                        <FormControl sx={{ my: 2, minWidth: 120 }} size="small" disabled={disabled}>
                            {criteriaConstraints[id].map((constraint, index) => (
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        marginBottom: 16,
                                    }}
                                    key={id + '_' + index}
                                >
                                    {constraint.operator && (
                                        <Select
                                            style={{ marginRight: 16 }}
                                            variant="outlined"
                                            value={constraint.operator}
                                            onChange={(e) => onConstraintOperatorChange(e.target.value as ('and' | 'or'), index)}
                                        >
                                            <MenuItem value="and">And</MenuItem>
                                            <MenuItem value="or">Or</MenuItem>
                                        </Select>
                                    )}
                                    <Select
                                        variant="outlined"
                                        value={constraint.rule}
                                        onChange={(e) => onConstraintChange(e.target.value, index, 'rule')}
                                    >
                                        <MenuItem value="em">Include if exact match</MenuItem>
                                        <MenuItem value="!em">Exclude if exact match</MenuItem>
                                        <MenuItem value="instr">Include if contains</MenuItem>
                                        <MenuItem value="!instr">exclude if contains</MenuItem>
                                    </Select>
                                    <TextField
                                        style={{ marginLeft: 16 }}
                                        value={constraint.value}
                                        onBlur={(e) => onConstraintChange(e.target.value, index, 'value')}
                                        size="small"
                                    />
                                    <IconButton style={{ marginLeft: '.5rem' }}
                                                onClick={() => onDeleteConstraint(index)}>
                                        <CloseIcon/>
                                    </IconButton>
                                </div>
                            ))}
                        </FormControl>
                    </div>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon/>}
                        size="small"
                        onClick={onAddConstraint}
                        disabled={disabled}
                    >
                        Add Constraint
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AppCriteriaTypeText;
